import { Box, Paper, Typography, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { type ReactNode, useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import { formatExportTimestamp, toExcelFilenameSlug, type ChartExcelData } from '../../utils/chartExcel';
import { ChartDownloadMenu } from './ChartDownloadMenu';

interface ChartCardProps {
	title: string;
	height?: number;
	minHeight?: number;
	fullHeight?: boolean;
	fullWidth?: boolean;
	children: ReactNode;
	additionalButtons?: ReactNode;
	excelData?: ChartExcelData;
}

export const ChartCard = ({ title, height = 400, minHeight, fullHeight = false, fullWidth = false, children, additionalButtons, excelData }: ChartCardProps) => {
	const chartKey = `chart-card-${title.replace(/\s+/g, '-').toLowerCase()}`;
	const chartRef = useRef<HTMLDivElement>(null);
	const downloadButtonRef = useRef<HTMLDivElement>(null);
	const [isDownloading, setIsDownloading] = useState(false);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

	const responsiveHeight = isMobile
		? Math.max(minHeight || 300, height * 0.7)
		: isTablet
			? Math.max(minHeight || 350, height * 0.85)
			: height;

	const handleDownloadPNG = useCallback(async () => {
		if (!chartRef.current || !downloadButtonRef.current) return;

		try {
			setIsDownloading(true);
			downloadButtonRef.current.style.display = 'none';

			await new Promise(resolve => setTimeout(resolve, 100));

			const canvas = await html2canvas(chartRef.current, {
				backgroundColor: '#ffffff',
				scale: 2,
				logging: false,
			});

			const link = document.createElement('a');
			// Same slug as the Excel download from this card: the old inline replace kept
			// diacritics, parentheses and the slash in "Tỉnh/Thành", which browsers then
			// mangled into an underscore — so the two files from one chart looked unrelated.
			link.download = `${toExcelFilenameSlug(title)}-${formatExportTimestamp()}.png`;
			link.href = canvas.toDataURL('image/png');
			link.click();
		} catch (err) {
			console.error('Error downloading chart:', err);
		} finally {
			if (downloadButtonRef.current) {
				downloadButtonRef.current.style.display = 'flex';
			}
			setIsDownloading(false);
		}
	}, [title]);

	return (
		<Paper
			key={chartKey}
			sx={{
				height: fullHeight ? '100%' : `${responsiveHeight}px`,
				width: fullWidth ? '100%' : 'auto',
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden'
			}}
			ref={chartRef}
		>
			{/* Chart Header */}
			<Box
				sx={{
					display: 'flex',
					alignItems: isMobile ? 'flex-start' : 'center',
					justifyContent: 'space-between',
					flexDirection: isMobile ? 'column' : 'row',
					p: { xs: 1.5, sm: 2, md: 3 },
					pb: { xs: 1, sm: 1.5, md: 2 },
					position: 'sticky',
					top: 0,
					bgcolor: 'background.paper',
					zIndex: 10,
					borderBottom: 1,
					borderColor: 'divider',
					flexWrap: 'wrap',
					gap: 1
				}}
			>
				<Typography
					variant={isMobile ? 'h6' : 'h5'}
					component="h2"
					sx={{
						fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
						flex: '1 1 0',
						minWidth: 0,
						wordBreak: 'break-word',
						overflowWrap: 'anywhere',
						lineHeight: 1.3
					}}
				>
					{title}
				</Typography>
				<Box
					ref={downloadButtonRef}
					sx={{
						display: 'flex',
						gap: 1,
						alignItems: 'center',
						flexShrink: 0,
						width: isMobile ? '100%' : 'auto',
						justifyContent: isMobile ? 'flex-start' : 'flex-end'
					}}
				>
					<Stack
						direction="row"
						spacing={0.5}
					>
						{additionalButtons}
						<ChartDownloadMenu
							variant="icon"
							onDownloadPng={handleDownloadPNG}
							excel={excelData}
							excelFilename={toExcelFilenameSlug(title)}
							disabled={isDownloading}
							size={isMobile ? 'small' : 'medium'}
						/>
					</Stack>
				</Box>
			</Box>
			<Box
				sx={{
					flex: 1,
					px: { xs: 1, sm: 2, md: 3 },
					pb: { xs: 1.5, sm: 2, md: 3 },
					overflowX: 'auto',
					overflowY: 'auto',
					minWidth: 0
				}}
			>
				{children}
			</Box>
		</Paper>
	);
};
